import { z } from "zod";
import {
  AVAILABILITY_STATUSES,
  BLOCKING_SEVERITIES,
  BOOKING_ACTIONS,
  BOOKING_CHANNELS,
  BOOKING_STATUSES,
  FLOW_STEPS,
  GUARANTEE_LEVELS,
  OPTION_KINDS,
  OPTION_SCOPES,
  PRICE_STATUSES,
  PRICING_UNITS,
  THEME_MODES,
} from "./contract";

/**
 * Runtime validation for everything crossing the bridge.
 *
 * The mock implementation is validated by the same schemas as the future HTTP one — a fixture
 * that would not survive the wire is a broken fixture, and finding that out now is the whole
 * point of building the widget ahead of the backend.
 */

const moneySchema = z.object({
  formatted: z.string(),
  currency: z.string().length(3),
});

// Swift's default Codable encoding omits nil optionals, while older mock fixtures represented
// the same wire value as explicit null. Accept both encodings at the boundary and normalize to
// null so the rest of the widget keeps one stable shape. Required commercial fields remain
// strict and are never defaulted here.
const nullable = <T extends z.ZodType>(schema: T) =>
  schema.nullish().transform((value): z.output<T> | null => value ?? null);

export const bookingOptionSchema = z.object({
  code: z.string().min(1),
  displayName: z.string().min(1),
  description: nullable(z.string()),
  scope: z.enum(OPTION_SCOPES),
  kind: z.enum(OPTION_KINDS),
  guaranteeLevel: z.enum(GUARANTEE_LEVELS),
  priceStatus: z.enum(PRICE_STATUSES),
  availabilityStatus: z.enum(AVAILABILITY_STATUSES),
  price: nullable(moneySchema),
  pricingUnit: nullable(z.enum(PRICING_UNITS)),
  conflictsWith: z.array(z.string()),
  requires: z.array(z.string()),
  maxQuantity: nullable(z.number().int().positive()),
});

const brandingSchema = z.object({
  accentColor: nullable(z.string()),
  themeMode: z.enum(THEME_MODES),
  logoUrl: nullable(z.string()),
  heroImageUrl: nullable(z.string()),
});

const hotelPresentationSchema = z.object({
  hotelId: z.string().min(1),
  name: z.string().min(1),
  city: nullable(z.string()),
  countryCode: nullable(z.string()),
  branding: brandingSchema,
});

export const hotelBootstrapSchema = z.object({
  hotel: hotelPresentationSchema,
  rules: z.object({
    parentBedMaxAge: nullable(z.number().int().nonnegative()),
    adultFromAge: z.number().int().positive(),
    requiresMinorAges: z.boolean(),
    maxRooms: z.number().int().positive(),
    maxNights: z.number().int().positive(),
    maxAdultsPerRoom: z.number().int().positive(),
  }),
  categories: z.array(
    z.object({
      code: z.string().min(1),
      displayName: z.string().min(1),
      description: nullable(z.string()),
      maxAdults: z.number().int().positive(),
      maxOccupancy: z.number().int().positive(),
      imageUrl: nullable(z.string()),
      guaranteedAttributes: z.array(bookingOptionSchema),
    }),
  ),
  rates: z.array(
    z.object({
      code: z.string().min(1),
      displayName: z.string().min(1),
      description: nullable(z.string()),
      mealPlanCode: z.string().min(1),
      mealPlanLabel: z.string().min(1),
      includedBenefits: z.array(z.string()),
      cancellationSummary: nullable(z.string()),
      paymentSummary: nullable(z.string()),
    }),
  ),
  preferences: z.array(bookingOptionSchema),
  addons: z.array(bookingOptionSchema),
  requirements: z.array(bookingOptionSchema),
  languages: z.array(z.string()).min(1),
  defaultLanguage: z.string(),
  currency: z.string().length(3),
});

const minorGuestSchema = z.object({
  id: z.string().min(1),
  age: nullable(z.number().int().nonnegative()),
  sleepsInParentBed: z.boolean(),
  countsAgainstCapacity: z.boolean(),
});

const roomProjectionSchema = z.object({
  roomId: z.string().min(1),
  adults: z.number().int().positive(),
  minors: z.array(minorGuestSchema),
  categoryCode: nullable(z.string()),
  rateProductCode: nullable(z.string()),
  requirements: z.array(z.string()),
  preferences: z.array(z.string()),
  availabilityStatus: z.enum(AVAILABILITY_STATUSES),
  categoryOptions: z.array(
    z.object({
      code: z.string(),
      selectable: z.boolean(),
      reason: nullable(z.string()),
    }),
  ),
  partyLabel: z.string(),
});

const quoteSchema = z.object({
  quoteId: z.string().min(1),
  version: z.number().int().positive(),
  fingerprint: z.string().min(1),
  issuedAt: z.string(),
  expiresAt: z.string(),
  roomLines: z.array(
    z.object({
      roomId: z.string(),
      categoryName: z.string(),
      rateName: z.string(),
      mealPlanLabel: z.string(),
      nights: z.array(z.object({ date: z.string(), price: moneySchema })).min(1),
      includedBenefits: z.array(z.string()),
      subtotal: moneySchema,
    }),
  ),
  addonLines: z.array(
    z.object({
      addonId: z.string(),
      displayName: z.string(),
      quantityLabel: z.string(),
      total: moneySchema,
    }),
  ),
  taxLines: z.array(
    z.object({ label: z.string(), total: moneySchema, note: nullable(z.string()) }),
  ),
  total: moneySchema,
  dueNow: moneySchema,
  dueLater: moneySchema,
  paymentSummary: z.string(),
  cancellationSummary: z.string(),
});

export const bookingProjectionSchema = z.object({
  flowId: z.string().min(1),
  revision: z.number().int().nonnegative(),
  status: z.enum(BOOKING_STATUSES),
  channel: z.enum(BOOKING_CHANNELS),
  hotel: hotelPresentationSchema,
  stay: nullable(
    z.object({
      checkin: z.string(),
      checkout: z.string(),
      nights: z.number().int().positive(),
    }),
  ),
  rooms: z.array(roomProjectionSchema),
  addons: z.array(
    z.object({
      addonId: z.string(),
      code: z.string(),
      displayName: z.string(),
      scope: z.enum(OPTION_SCOPES),
      roomId: nullable(z.string()),
      quantity: z.number().int().positive(),
      fromDate: nullable(z.string()),
      toDate: nullable(z.string()),
      guaranteeLevel: z.enum(GUARANTEE_LEVELS),
      priceStatus: z.enum(PRICE_STATUSES),
    }),
  ),
  contact: nullable(
    z.object({
      firstName: nullable(z.string()),
      lastName: nullable(z.string()),
      email: nullable(z.string()),
      phone: nullable(z.string()),
      isBusinessTrip: z.boolean(),
      companyName: nullable(z.string()),
    }),
  ),
  availability: nullable(
    z.object({
      status: z.enum(AVAILABILITY_STATUSES),
      resolvedAt: z.string(),
      categories: z.array(
        z.object({
          code: z.string(),
          status: z.enum(AVAILABILITY_STATUSES),
          remainingHint: nullable(z.string()),
          fromPrice: nullable(moneySchema),
        }),
      ),
      autoSelectionNote: nullable(z.string()),
    }),
  ),
  quote: nullable(quoteSchema),
  payment: nullable(
    z.object({
      state: z.string(),
      dueNow: nullable(moneySchema),
      provider: nullable(z.string()),
    }),
  ),
  blockingReasons: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
      severity: z.enum(BLOCKING_SEVERITIES),
      step: nullable(z.enum(FLOW_STEPS)),
      roomId: nullable(z.string()),
      field: nullable(z.string()),
    }),
  ),
  allowedActions: z.array(z.enum(BOOKING_ACTIONS)),
  expiresAt: nullable(z.string()),
});

export const checkoutSessionSchema = z.object({
  checkoutSessionId: z.string().min(1),
  clientSecret: z.string().min(1),
  publishableKey: z.string().min(1),
  mode: z.enum(["sandbox", "live"]),
  quoteFingerprint: z.string().min(1),
  expiresAt: z.string(),
});

export const bookingStatusReportSchema = z.object({
  flowId: z.string().min(1),
  status: z.enum(BOOKING_STATUSES),
  reservationIds: z.array(z.string()),
  message: nullable(z.string()),
});
